import type { Map as MapLibreMap } from "maplibre-gl";

type MapTilerDatestampTileJson = { datestamp: string };
type MapTilerV3OpenMapTilesJson = { planettime: string };
type TracestrackVersionJson = { map_date: string };

type FetchJson = (url: string) => Promise<unknown>;

// A fetcher that returns the snapshot datetime for a tileset, given a vector tileset style.json URL.
type TilesetFreshnessFetcher = (
  tileJsonUrl: string,
  fetchJson: FetchJson,
) => Promise<string | undefined>;

const TILESET_FRESHNESS_FETCHERS: Record<
  string,
  TilesetFreshnessFetcher
> = {
  ["https://api.maptiler.com/tiles/v3-openmaptiles/tiles.json"]:
    fetchMapTilerV3OpenMapTiles,
  ["https://api.maptiler.com/tiles/v3/tiles.json"]: fetchMapTiler,
  ["https://api.maptiler.com/tiles/v4/tiles.json"]: fetchMapTiler,
  ["https://tile.tracestrack.com/v/maps/carto_v2/tilejson.json"]:
    fetchTracestrackCarto,
};

const freshnessLabelCache = new Map<string, string>();

export async function freshnessLabelForMap(
  map: MapLibreMap,
): Promise<string> {
  const tileJsonUrl = getTileJsonUrl(map);
  if (!tileJsonUrl) return "";

  const cached = freshnessLabelCache.get(tileJsonUrl);
  if (cached !== undefined) return cached;

  const fetcher = TILESET_FRESHNESS_FETCHERS[tilesetFreshnessKey(tileJsonUrl)];
  if (!fetcher) {
    console.warn(
      `No tileset freshness handler for tileset URL: ${tileJsonUrl}`,
    );
    return "";
  }
  const datetime = await fetcher?.(tileJsonUrl, defaultFetchJson);
  const label = formatFreshness(datetime);

  freshnessLabelCache.set(tileJsonUrl, label);
  return label;
}

function getTileJsonUrl(map: MapLibreMap): string | undefined {
  return Object.values(map.getStyle().sources)
    .filter((source) => source.type === "vector")
    .map((source) => source.url)
    .find((url): url is string => !!url);
}

function formatFreshness(datetime?: string): string {
  return datetime
    ? new Date(datetime).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    })
    : "";
}

async function defaultFetchJson(url: string): Promise<unknown> {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to fetch tileset metadata: ${response.status}`);
  }
  return response.json();
}

async function fetchTracestrackCarto(
  _tileJsonUrl: string,
  fetchJson: FetchJson,
): Promise<string | undefined> {
  const data = await fetchJson(
    "https://tile.tracestrack.com/v/maps/carto/version.json",
  ) as TracestrackVersionJson;

  return data.map_date.trim() || undefined;
}

async function fetchMapTiler(
  tileJsonUrl: string,
  fetchJson: FetchJson,
): Promise<string | undefined> {
  const data = await fetchJson(tileJsonUrl) as MapTilerDatestampTileJson;
  return data.datestamp.trim() || undefined;
}

async function fetchMapTilerV3OpenMapTiles(
  tileJsonUrl: string,
  fetchJson: FetchJson,
): Promise<string | undefined> {
  const data = await fetchJson(tileJsonUrl) as MapTilerV3OpenMapTilesJson;
  const planetTime = Number(data.planettime);

  if (Number.isFinite(planetTime)) {
    return new Date(planetTime).toISOString();
  }

  return undefined;
}

function tilesetFreshnessKey(tileJsonUrl: string): string {
  const url = new URL(tileJsonUrl);
  // Style source URLs include provider API keys in query params. The freshness
  // mapping is keyed by the stable tileset endpoint so it works across envs.
  return `${url.origin}${url.pathname}`;
}
